import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentUser } from 'store/user/selectors';

import { Provider } from 'services/Analytics/core';
import { getAbTestName } from 'services/Analytics/helpers';
import { getAbAutoTestsParams } from 'services/Firebase/helpers';
import useVariant from 'services/Firebase/hooks/useVariant';
import { ActiveGroup } from 'services/Firebase/utils/experiments';

const getGroupFromConfig = (percent: number, config: Record<string, number>): string => {
    let sum = 0;

    for (const [variant, weight] of Object.entries(config)) {
        sum += weight;
        if (percent < sum) return variant;
    }

    return ActiveGroup.Control;
};

export const useExperimentGroup = (experimentKey: string) => {
    const { abTestPercentage } = useSelector(selectCurrentUser);
    const configString = useVariant(experimentKey);
    const { aqa, variantName: aqaVariantName, experiment: aqaExperiment } = getAbAutoTestsParams();

    const initialGroup = useMemo(() => {
        if (aqa && aqaVariantName) {
            return aqaVariantName;
        }

        if (typeof abTestPercentage === 'number' && configString) {
            try {
                const config = JSON.parse(configString) as Record<string, number>;

                return getGroupFromConfig(abTestPercentage, config);
            } catch {
                return ActiveGroup.Control;
            }
        }

        return ActiveGroup.Control;
    }, [aqa, aqaVariantName, abTestPercentage, configString]);

    const [group, setGroup] = useState<string>(initialGroup);

    useEffect(() => {
        const testName = aqa && aqaExperiment ? aqaExperiment : experimentKey;

        if (configString || aqa) {
            Provider.provideData({
                abTestName: async () => getAbTestName({ experiment: testName, variantName: initialGroup }),
            });
        }

        setGroup(initialGroup);
    }, [initialGroup, experimentKey, aqa, aqaExperiment, configString]);

    return group;
};
